import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getImageUrl } from '../utils/imageHelpers';

interface ImageDropzoneProps {
  maxImages?: number;
  existingImages?: string[]; // Array of image filenames or full URLs from backend
  onImagesChange: (files: File[]) => void;
  onRemoveExisting?: (index: number) => void;
  accept?: string;
  className?: string;
  label?: string;
}

const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  maxImages = 1,
  existingImages = [],
  onImagesChange,
  onRemoveExisting,
  accept = 'image/*',
  className = '',
  label,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);


  // Handle file selection
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];
    const remainingSlots = maxImages - existingImages.length - selectedFiles.length;

    Array.from(files).slice(0, remainingSlots).forEach((file) => {
      if (file.type.startsWith('image/')) {
        newFiles.push(file);
        newPreviews.push(URL.createObjectURL(file));
      }
    });

    if (newFiles.length > 0) {
      const updatedFiles = [...selectedFiles, ...newFiles];
      const updatedPreviews = [...previews, ...newPreviews];
      setSelectedFiles(updatedFiles);
      setPreviews(updatedPreviews);
      onImagesChange(updatedFiles);
    }
  }, [maxImages, existingImages.length, selectedFiles, previews, onImagesChange]);

  // Handle file input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Remove selected file
  const removeSelectedFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    
    // Revoke object URL to prevent memory leak
    URL.revokeObjectURL(previews[index]);
    
    setSelectedFiles(updatedFiles);
    setPreviews(updatedPreviews);
    onImagesChange(updatedFiles);
  };

  // Remove existing image
  const handleRemoveExisting = (index: number) => {
    if (onRemoveExisting) {
      onRemoveExisting(index);
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [previews]);

  const totalImages = existingImages.length + selectedFiles.length;
  const canAddMore = totalImages < maxImages;

  return (
    <div className={className}>
      {label && (
        <label className="fs-6 fw-semibold mb-3 d-block">
          {label}
          {maxImages > 1 && (
            <span className="text-muted fs-7 ms-2">({totalImages}/{maxImages})</span>
          )}
        </label>
      )}

      {/* Existing Images */}
      {existingImages.length > 0 && (
        <div className="d-flex flex-wrap gap-3 mb-3">
          {existingImages.map((image, index) => (
            <div key={`existing-${index}`} className="position-relative" style={{ width: '150px', height: '150px' }}>
              <img
                src={getImageUrl(image)}
                alt={`Existing ${index + 1}`}
                className="w-100 h-100 object-cover rounded border"
                style={{ objectFit: 'cover' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/media/blank.png';
                }}
              />
              {onRemoveExisting && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-danger position-absolute top-0 end-0 m-1"
                  onClick={() => handleRemoveExisting(index)}
                  title="Remove image"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selected Files Previews */}
      {selectedFiles.length > 0 && (
        <div className="d-flex flex-wrap gap-3 mb-3">
          {previews.map((preview, index) => (
            <div key={`preview-${index}`} className="position-relative" style={{ width: '150px', height: '150px' }}>
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="w-100 h-100 object-cover rounded border"
                style={{ objectFit: 'cover' }}
              />
              <button
                type="button"
                className="btn btn-sm btn-icon btn-danger position-absolute top-0 end-0 m-1"
                onClick={() => removeSelectedFile(index)}
                title="Remove image"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dropzone */}
      {canAddMore && (
        <div
          className={`border border-dashed border-gray-300 rounded p-5 text-center ${
            isDragging ? 'border-primary bg-light-primary' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="mb-3">
            <i className="fas fa-cloud-upload-alt fs-3x text-gray-400"></i>
          </div>
          <div className="text-gray-600 mb-2">
            <span className="text-primary fw-semibold">Click to upload</span> or drag and drop
          </div>
          <div className="text-gray-500 fs-7">
            {maxImages === 1 ? 'PNG, JPG, JPEG up to 10MB' : `PNG, JPG, JPEG up to 10MB (${maxImages - totalImages} more allowed)`}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={maxImages > 1}
            onChange={handleInputChange}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {!canAddMore && totalImages > 0 && (
        <div className="alert alert-info">
          Maximum {maxImages} {maxImages === 1 ? 'image' : 'images'} reached. Remove an image to add more.
        </div>
      )}
    </div>
  );
};

export default ImageDropzone;

